import React from 'react'
import { ReactComponent as SecurityShieldIcon } from '@/assets/application-flow-skins/shared/icon-security-shield.svg'
import { ApplicationFlowHeader, ApplicationFlowRetentionDialog } from '../components'
import { FormSkinOption, FormSkinProps } from '../types'
import themeStyles from '../theme/application-flow-theme.module.scss'
import { FormSkinController } from './hooks/use-form-skin-controller'
import styles from './form-skin.module.scss'

interface Props extends FormSkinProps {

  /** 仅管理展开、校验提示和协议弹窗的皮肤本地交互。 */
  controller: FormSkinController
}

interface OptionGridProps {

  /** 展示给用户的分组标题。 */
  label: string

  /** 来自旧枚举的可选项，不由皮肤猜测 value。 */
  options: readonly FormSkinOption[]

  /** 当前选中的旧枚举值。 */
  value: string

  /** 用户切换选项时回传旧枚举值。 */
  onChange: (value: string) => void
}

interface BoostOptionProps {

  /** 展示给用户的资质名称。 */
  label: string

  /** 当前资质是否已由旧容器选中。 */
  selected: boolean

  /** 是否展示“推荐”角标。 */
  recommended?: boolean

  /** 用户切换资质时委托旧容器更新旧枚举。 */
  onClick: () => void
}

const OptionGrid: React.FC<OptionGridProps> = React.memo(({ label, onChange, options, value }) => (
  <section className={styles.optionSection}>
    <h2>{label}</h2>
    <div className={styles.optionGrid}>
      {options.map(option => (
        <button
          aria-pressed={option.value === value}
          className={option.value === value ? styles.optionSelected : styles.option}
          key={option.value}
          onClick={() => onChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  </section>
))

OptionGrid.displayName = 'FormSkinOptionGrid'

const BoostOption: React.FC<BoostOptionProps> = React.memo(({ label, onClick, recommended = false, selected }) => (
  <button
    aria-pressed={selected}
    className={selected ? styles.boostOptionSelected : styles.boostOption}
    onClick={onClick}
    type="button"
  >
    {recommended ? <span aria-hidden="true" className={styles.recommended}>优质</span> : null}
    {label}
  </button>
))

BoostOption.displayName = 'FormSkinBoostOption'

/**
 * FormSkin — 申请资料填写阶段的纯展示皮肤。
 * @returns {React.ReactElement} 表单展示节点；所有业务动作均由 Props 注入
 */
const FormSkin: React.FC<Props> = React.memo(({
  controller,
  model,
  onBack,
  onChangeApplyLimit,
  onChangeIdentity,
  onChangeRealName,
  onChangeSesameCredit,
  onOpenCityPicker,
  onToggleBusinessLicense,
  onToggleCarProperty,
  onToggleHouseProperty,
  onToggleHousingFund,
  onTogglePublicSectorJob,
  onToggleSocialSecurity,
  theme,
}) => (
  <main className={themeStyles.themeRoot} data-theme={theme.name}>
    <section className={styles.page}>
      <img alt="" aria-hidden="true" className={styles.headerBackground} height="351" src={theme.formHeader} width="375" />
      <ApplicationFlowHeader onBack={controller.onBack} title="借款" visualType="transparent" />
      <div className={styles.content}>
        <h1 className={styles.srOnly}>借款申请</h1>
        <section className={`${styles.limitCard} ${controller.limitEditorOpen ? styles.limitCardExpanded : ''}`}>
          <span className={styles.label}>资金需求</span>
          <button
            aria-expanded={controller.limitEditorOpen}
            className={styles.limitValue}
            onClick={controller.onToggleLimitEditor}
            type="button"
          >
            <strong><i>¥</i>{controller.applyLimitDisplay}</strong>
            <img alt="编辑额度" height="19" src={theme.formEditLimitIcon} width="20" />
          </button>
          <p>请选择您的资金需求且如实填写以下信息，<em>优质资产额度更高哦</em></p>
          {controller.limitEditorOpen ? (
            <div className={styles.limitEditor}>
              <h2>请选择您的资金需求</h2>
              <div className={styles.limitOptionGrid}>
                {model.applyLimitOptions.map(option => (
                  <button
                    aria-pressed={option.value === model.applyLimit}
                    className={option.value === model.applyLimit ? styles.limitOptionSelected : styles.limitOption}
                    key={option.value}
                    onClick={() => onChangeApplyLimit(option.value)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <section className={styles.formCard}>
          <button className={styles.fieldButton} onClick={onOpenCityPicker} type="button">
            <span><em>*</em>申请城市</span>
            <strong>{model.cityName || '请选择'}</strong>
            <i aria-hidden="true">›</i>
          </button>
          <label className={`${styles.inputField} ${controller.realNameInvalid ? styles.inputError : ''}`}>
            <span><em>*</em>真实姓名</span>
            <input
              aria-label="真实姓名"
              autoComplete="name"
              name="realName"
              onBlur={event => controller.onValidateRealName(event.target.value)}
              onChange={event => {
                onChangeRealName(event.target.value)
                controller.onValidateRealName(event.target.value)
              }}
              placeholder={controller.realNameErrorInfo.placeholder}
              spellCheck={false}
              value={model.realName}
            />
            {controller.realNameErrorInfo.errorTip ? <span className={styles.errorTip}>{controller.realNameErrorInfo.errorTip}</span> : null}
          </label>
          <label className={`${styles.inputField} ${controller.identityInvalid ? styles.inputError : ''}`}>
            <span><em>*</em>身份证号</span>
            <input
              aria-label="身份证号"
              autoComplete="off"
              inputMode="text"
              maxLength={18}
              name="identity"
              onBlur={event => controller.onValidateIdentity(event.target.value)}
              onChange={event => {
                const identity = event.target.value.replace(/[^0-9xX]/g, '').toUpperCase()
                onChangeIdentity(identity)
                controller.onValidateIdentity(identity)
              }}
              placeholder={controller.identityErrorInfo.placeholder}
              spellCheck={false}
              value={model.identity}
            />
            {controller.identityErrorInfo.errorTip ? <span className={styles.errorTip}>{controller.identityErrorInfo.errorTip}</span> : null}
          </label>

          <OptionGrid label="芝麻分信用分" onChange={onChangeSesameCredit} options={model.sesameCreditOptions} value={model.sesameCredit} />
          <section className={styles.boostSection}>
            <h2>提额资质 <span>（支持多选，可提高授信额度）</span></h2>
            <div className={styles.boostGrid}>
              <BoostOption label="有房" onClick={onToggleHouseProperty} recommended selected={model.houseProperty === '1'} />
              <BoostOption label="有车" onClick={onToggleCarProperty} recommended selected={model.carProperty === '1'} />
              <BoostOption label="有公积金" onClick={onToggleHousingFund} recommended selected={model.housingFund === '2'} />
              <BoostOption label="有社保" onClick={onToggleSocialSecurity} selected={model.socialSecurity === '2'} />
              <BoostOption label="有营业执照" onClick={onToggleBusinessLicense} selected={model.job === '30'} />
              <BoostOption label="公务员/事业单位/国企" onClick={onTogglePublicSectorJob} selected={model.job === '40'} />
            </div>
          </section>
        </section>
        <p className={styles.securityTip}>
          <SecurityShieldIcon aria-hidden="true" />
          <span>仅用于匹配合适借款产品，将加密保障您的信息安全</span>
        </p>
      </div>

      <footer className={styles.footer}>
        <button className={styles.primaryButton} disabled={model.submitting} onClick={controller.onSubmit} type="button">
          {model.submitting ? '提交中' : '去借钱'}
        </button>
      </footer>

      <ApplicationFlowRetentionDialog
        canClose={controller.retentionCanClose}
        illustrationSrc={theme.formRetentionIllustration}
        onDismiss={controller.onCloseRetention}
        onLeave={controller.onConfirmLeave}
        onSubmit={controller.onSubmitFromRetention}
        submitting={model.submitting}
        visible={controller.retentionVisible}
      />
    </section>
  </main>
))

FormSkin.displayName = 'FormSkin'

export default FormSkin
